import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { todayIso, addDaysIso } from '@/lib/today'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useInvoices } from '@/hooks/use-invoices'
import { useClients, clientName } from '@/hooks/use-clients'
import { money } from '@/lib/format'
import { Page, PageHeader, Card, Table, Trow, StatusPill, TabPills, KpiCard, Pagination, SearchInput, SkeletonRows, EmptyState, btnPrimary, btnSecondary } from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/invoices')({
  component: InvoicesPage,
  // Reports links in with ?aging=<bucket>. Anything else is ignored rather than rejected: a bad
  // link in an email should still open the invoice list.
  // Returns {} rather than { aging: undefined } so the param stays OPTIONAL. With the key always
  // present, every existing navigate({ to: '/invoices' }) in the app fails to typecheck.
  validateSearch: (s: Record<string, unknown>): { aging?: string } =>
    typeof s.aging === 'string' && AGING_BUCKETS.includes(s.aging) ? { aging: s.aging } : {},
})

const AGING_BUCKETS = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus']
const AGING_LABEL: Record<string, string> = {
  current: 'Not yet due', d1_30: '1–30 days overdue', d31_60: '31–60 days overdue',
  d61_90: '61–90 days overdue', d90_plus: '90+ days overdue',
}

const COLS = '90px 1.5fr 120px 110px 110px 120px'
const PAGE = 15
// PostgREST or() filters are comma/paren delimited — strip those from user input.
const safe = (s: string) => s.replace(/[,()]/g, ' ').trim()
const shortDate = (iso: string | null) => (iso ? phFormat(new Date(iso), 'date', { day: 'numeric', month: 'short' }) : '—')
const overdueDays = (due: string | null) => (due ? Math.floor((Date.now() - new Date(due).getTime()) / 86400000) : 0)
const sameMonth = (iso: string | null) => { if (!iso) return false; const d = new Date(iso), n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear() }

/**
 * Tab filters, server-side. `balance` is a generated column (total - amount_paid) added
 * precisely so these can run in PostgREST, which cannot compare two columns. The
 * conditions below mirror the old client-side isPaid()/isOverdue() helpers exactly.
 */
const NOT_BILLABLE = '(paid,void,draft)'
function applyTab<T extends { or: (f: string) => T; gt: (c: string, v: unknown) => T; not: (c: string, o: string, v: unknown) => T; lt: (c: string, v: unknown) => T }>(q: T, tab: string): T {
  if (tab === 'paid') return q.or('status.eq.paid,balance.lte.0')
  if (tab === 'overdue') return q.gt('balance', 0).not('status', 'in', NOT_BILLABLE).lt('due_date', todayIso())
  if (tab === 'awaiting') return q.gt('balance', 0).not('status', 'in', NOT_BILLABLE).or(`due_date.is.null,due_date.gte.${todayIso()}`)
  return q
}

/**
 * 014 T003. The same buckets invoice_aging() produces, expressed as PostgREST filters.
 *
 * The boundaries must match the RPC or a bucket showing $3,586 would open a list that does not
 * contain it. Both use the local date, which is what todayIso() now returns.
 */
function applyAging<T extends { gt: (c: string, v: unknown) => T; not: (c: string, o: string, v: unknown) => T; or: (f: string) => T; gte: (c: string, v: unknown) => T; lte: (c: string, v: unknown) => T; lt: (c: string, v: unknown) => T }>(q: T, bucket: string | undefined): T {
  if (!bucket) return q
  const today = todayIso()
  const base = q.gt('balance', 0).not('status', 'in', NOT_BILLABLE)
  if (bucket === 'current')  return base.or(`due_date.is.null,due_date.gte.${today}`)
  if (bucket === 'd1_30')    return base.gte('due_date', addDaysIso(today, -30)).lt('due_date', today)
  if (bucket === 'd31_60')   return base.gte('due_date', addDaysIso(today, -60)).lt('due_date', addDaysIso(today, -30))
  if (bucket === 'd61_90')   return base.gte('due_date', addDaysIso(today, -90)).lt('due_date', addDaysIso(today, -60))
  return base.lt('due_date', addDaysIso(today, -90))
}

interface InvoiceRow {
  id: number
  invoice_number: string | null
  title: string | null
  status: string
  total: number
  amount_paid: number
  balance: number
  currency: string | null
  issue_date: string | null
  due_date: string | null
  client: { id: string; first_name: string | null; last_name: string | null; organization: string | null } | null
}

function InvoicesPage() {
  const navigate = useNavigate()
  const { create } = useInvoices()
  const { clients } = useClients()
  const { aging } = Route.useSearch()
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState('')
  const reset = () => setPage(0)

  // KPI totals genuinely need every row — a paginated page can't sum them. Fetch only the
  // six columns the sums need rather than whole invoices.
  const kpiQ = useQuery({
    queryKey: ['invoices', 'kpi'],
    queryFn: async () => {
      const { data, error } = await supabase.from('customer_invoice')
        .select('total, amount_paid, balance, status, due_date, paid_at, issue_date')
        .is('deleted_at', null)
      if (error) throw error
      const today = todayIso()
      let paidMonth = 0, awaiting = 0, overdue = 0
      for (const r of (data ?? []) as Array<Record<string, unknown>>) {
        const bal = Number(r.balance ?? 0)
        const status = String(r.status ?? '')
        const paid = status === 'paid' || bal <= 0
        if (paid) {
          if (sameMonth((r.paid_at as string) ?? (r.issue_date as string))) paidMonth += Number(r.amount_paid || r.total)
        } else if (status !== 'draft' && status !== 'void') {
          const due = r.due_date as string | null
          if (due && due < today) overdue += bal
          else awaiting += bal
        }
      }
      return { paidMonth, awaiting, overdue }
    },
  })
  const kpi = kpiQ.data ?? { paidMonth: 0, awaiting: 0, overdue: 0 }

  // Tab counts — cheap head queries, independent of the current page.
  const counts = useQuery({
    queryKey: ['invoices', 'counts'],
    queryFn: async () => {
      const head = { count: 'exact' as const, head: true }
      const base = () => supabase.from('customer_invoice').select('id', head).is('deleted_at', null)
      const [all, overdue, awaiting, paid] = await Promise.all([
        base(),
        applyTab(base(), 'overdue'),
        applyTab(base(), 'awaiting'),
        applyTab(base(), 'paid'),
      ])
      return { all: all.count ?? 0, overdue: overdue.count ?? 0, awaiting: awaiting.count ?? 0, paid: paid.count ?? 0 }
    },
  })

  // Server-side filtered + paginated page (mirrors the jobs list).
  const invoicesPage = useQuery({
    queryKey: ['invoices', 'page', { filter, search, page, aging }],
    queryFn: async () => {
      let q = supabase.from('customer_invoice')
        .select('id, invoice_number, title, status, total, amount_paid, balance, currency, issue_date, due_date, client:contact(id, first_name, last_name, organization)', { count: 'exact' })
        .is('deleted_at', null)
      q = applyTab(q, filter)
      q = applyAging(q, aging)

      const s = safe(search)
      if (s) {
        // Client names live on the joined contact row; resolve matching ids locally and
        // fold them into the same or() so search-by-client-name keeps working.
        const ids = clients.filter((c) => clientName(c).toLowerCase().includes(s.toLowerCase())).map((c) => c.id)
        const parts = [`invoice_number.ilike.%${s}%`, `title.ilike.%${s}%`]
        if (ids.length) parts.push(`contact_id.in.(${ids.join(',')})`)
        q = q.or(parts.join(','))
      }

      const { data, error, count } = await q
        .order('created_at', { ascending: false })
        .range(page * PAGE, page * PAGE + PAGE - 1)
      if (error) throw error
      return { rows: (data ?? []) as unknown as InvoiceRow[], count: count ?? 0 }
    },
  })
  const rows = invoicesPage.data?.rows ?? []
  const c = counts.data
  const isOverdueRow = (i: InvoiceRow) =>
    Number(i.balance) > 0 && i.status !== 'paid' && i.status !== 'void' && i.status !== 'draft' && !!i.due_date && i.due_date < todayIso()

  const submit = async () => {
    if (!title.trim()) return toast.error('Enter a title.')
    try {
      const inv = await create.mutateAsync({ title: title.trim(), client_id: clientId || null })
      setTitle(''); setClientId(''); setOpen(false)
      navigate({ to: '/invoices/$id', params: { id: String(inv.id) } })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  return (
    <Page>
      <PageHeader
        title="Invoices"
        subtitle={`${invoicesPage.data?.count ?? 0} invoices in this view`}
        actions={<button className={btnPrimary} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New invoice</button>}
      />
      <div className="mb-[22px] mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <KpiCard label="Paid · this month" value={money(kpi.paidMonth, "AUD", { whole: true })} tone="green" />
        <KpiCard label="Awaiting payment" value={money(kpi.awaiting, "AUD", { whole: true })} />
        <KpiCard label="Overdue" value={money(kpi.overdue, "AUD", { whole: true })} tone="red" />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <TabPills active={filter} onChange={(v) => { setFilter(v); reset() }} chips={[
          { id: 'all', label: 'All', count: c?.all },
          { id: 'overdue', label: 'Overdue', count: c?.overdue },
          { id: 'awaiting', label: 'Awaiting', count: c?.awaiting },
          { id: 'paid', label: 'Paid', count: c?.paid },
        ]} />
        <div className="flex flex-1 items-center justify-end gap-2">
          {/* Arrived from a Reports bucket. Say which, and make it one click to get back out —
              a filter you cannot see is a list that looks wrong. */}
          {aging && (
            <button
              type="button"
              onClick={() => navigate({ to: '/invoices', search: {} })}
              className="inline-flex items-center gap-1.5 rounded-full border border-token-warning bg-token-warning-surface px-3 py-1.5 text-[12.5px] font-semibold text-token-warning transition-colors hover:bg-token-warning-surface"
              title="Clear this filter"
            >
              {AGING_LABEL[aging] ?? aging}
              <span aria-hidden className="text-[14px] leading-none">×</span>
            </button>
          )}
          <SearchInput value={search} onChange={(v) => { setSearch(v); reset() }} placeholder="Search invoices…" />
        </div>
      </div>

      {invoicesPage.isLoading ? (
        <Card><SkeletonRows count={5} /></Card>
      ) : rows.length === 0 ? (
        <Card><EmptyState label={(c?.all ?? 0) === 0 ? 'No invoices yet.' : 'No invoices match these filters.'} cta={(c?.all ?? 0) === 0 ? 'New invoice' : undefined} onCta={() => setOpen(true)} /></Card>
      ) : (
        <Table cols={COLS} header={<><span>Invoice</span><span>Client</span><span className="text-right">Amount · inc VAT</span><span>Issued</span><span>Due</span><span className="text-right">Status</span></>}>
          {rows.map((inv, idx) => {
            const od = isOverdueRow(inv)
            return (
              <Trow key={inv.id} cols={COLS} last={idx === rows.length - 1} onClick={() => navigate({ to: '/invoices/$id', params: { id: String(inv.id) } })}>
                <span className="font-display font-bold text-token-primary">{inv.invoice_number}</span>
                <span className="min-w-0">
                  <span className="block truncate font-bold text-token-primary">{inv.client ? clientName(inv.client) : '—'}</span>
                  {inv.title && <span className="block truncate text-[12px] font-normal text-token-secondary">{inv.title}</span>}
                </span>
                <span className="font-display text-right font-bold tabular-nums text-token-primary">{money(inv.total, inv.currency, { whole: true })}</span>
                <span className="text-token-secondary">{shortDate(inv.issue_date)}</span>
                <span className={od ? 'font-bold text-token-danger' : 'text-token-secondary'}>{od ? `Overdue ${overdueDays(inv.due_date)}d` : shortDate(inv.due_date)}</span>
                <span className="flex justify-end"><StatusPill status={od ? 'overdue' : inv.status} /></span>
              </Trow>
            )
          })}
          <Pagination page={page} pageSize={PAGE} total={invoicesPage.data?.count ?? 0} onPage={setPage} />
        </Table>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New invoice"
        footer={<><button className={btnSecondary} onClick={() => setOpen(false)}>Cancel</button><button className={btnPrimary} onClick={submit} disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create & edit'}</button></>}>
        <div className="space-y-4">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Switchboard upgrade" /></Field>
          <Field label="Client">
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
            </Select>
          </Field>
        </div>
      </Modal>
    </Page>
  )
}
